import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { uuidv7 } from 'uuidv7';
import { z } from 'zod';
import { DateTime } from 'luxon';
import { AccountState, AccountType } from '../api/account-types';
import { TransactionStatus, TransactionType } from '../../transaction/api/transaction-types';
import { useTimeZone } from '../../user/api/use-get-user';
import { useBudgetId } from '../../budget/budget-store';
import { useAccounts } from '../api/use-get-accounts';
import { useCreateAccountApi } from '../api/use-create-account';
import { useUpdateAccountApi } from '../api/use-update-account';
// Account Form
export const accountFormSchema = z.object({
    type: z.nativeEnum(AccountType),
    name: z.string().min(1, 'Account name is required'),
    offBudgetGroup: z.string().optional(),
    externalAccountId: z.string().uuid().optional(),
    balance: z.coerce.number(),
    notes: z.string().optional(),
});
/**
 * Determines the appropriate placeholder text for the off-budget group field
 * based on the selected account type.
 *
 * @param accountType - The currently selected account type
 * @returns The placeholder text for the off-budget group field
 */
function getOffBudgetGroupPlaceholder(accountType) {
    switch (accountType) {
        case AccountType.OFF_BUDGET_ASSET:
        case AccountType.OFF_BUDGET_LIABILITY:
            return 'Off-Budget';
        case AccountType.CREDIT_CARD:
            return 'Credit Cards';
        case AccountType.CASH:
        case AccountType.CHECKING:
        case AccountType.SAVINGS:
            return 'Liquid Accounts';
        default:
            return 'Optional group name';
    }
}
// Account Form Hook
export const useAccountForm = ({ account, onSuccess }) => {
    const timeZone = useTimeZone();
    const budgetId = useBudgetId();
    const accounts = useAccounts();
    const accountCount = accounts.length;
    // mutations
    const { mutate: createAccount, isPending: isCreatePending } = useCreateAccountApi({ onSuccess });
    const { mutate: updateAccount, isPending: isUpdatePending } = useUpdateAccountApi({ onSuccess });
    // enhance schema
    const schema = accountFormSchema.refine((data) => {
        const isNameTaken = accounts.some((v) => v.name.toLowerCase() === data.name.toLowerCase() && v.id !== account?.id);
        return !isNameTaken;
    }, {
        message: 'An account with this name already exists',
        path: ['name'],
    });
    const defaultValues = useMemo(() => toFormData(account), [account]);
    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues,
    });
    useEffect(() => {
        form.reset(defaultValues);
    }, [account, form]);
    // watch the account type to determine the placeholder
    const accountType = form.watch('type');
    const offBudgetGroupPlaceholder = useMemo(() => getOffBudgetGroupPlaceholder(accountType), [accountType]);
    /**
     * Handles the form submission event, building the API payload and
     * invoking the correct create or update mutation.
     *
     * @param values - Sanitized account form values.
     */
    const submitForm = useCallback((values) => {
        const payload = toRequestData(values, account, budgetId, accountCount, timeZone);
        if (account) {
            updateAccount(payload);
        }
        else {
            createAccount(payload);
        }
    }, [account, budgetId, accountCount, timeZone, createAccount, updateAccount]);
    return {
        form,
        onSubmit: form.handleSubmit(submitForm),
        isSubmitting: isCreatePending || isUpdatePending,
        offBudgetGroupPlaceholder,
    };
};
/**
 * Compute the initial values for the AccountForm.
 *
 * If `account` is provided (edit mode), it will map its fields into the form;
 * otherwise (create mode) it returns blank‐form defaults.
 *
 * @param account Optional existing Account to edit
 * @returns A fully populated AccountForm
 */
function toFormData(account) {
    return {
        type: account?.type ?? AccountType.CASH,
        name: account?.name ?? '',
        offBudgetGroup: account?.offBudgetGroup,
        externalAccountId: account?.externalAccountId,
        balance: 0,
        notes: account?.notes,
    };
}
/**
 * Build the AccountRequestAdd payload from form values and context.
 *
 * - Injects *`id`*, *`budgetId`*, *`state`*, and *`position`*.
 * - Adds an initial adjustment transaction if creating a new, non-off-budget account with a nonzero balance.
 * - Adds an initial negative assignment if creating a new, non-off-budget account with a negative balance.
 *
 * @param formValues - The values from the account form.
 * @param existing - The existing Account being edited, or undefined when creating.
 * @param budgetId - Current budget identifier.
 * @param accountCount - Number of existing accounts (for default position).
 * @param timeZone - User's IANA timezone for correct date handling.
 * @returns A fully populated AccountRequestAdd for create/update.
 */
function toRequestData(formValues, existing, budgetId, accountCount, timeZone) {
    const now = DateTime.now().setZone(timeZone);
    const isCreditCard = formValues.type === AccountType.CREDIT_CARD;
    const isNew = !existing;
    const accountId = existing ? existing.id : uuidv7();
    const balance = formValues.balance;
    // Construct transactions and assignments using the helper methods
    const transactions = constructTransactions(isNew, balance, accountId, budgetId, now);
    const assignments = constructAssignments(isNew, isCreditCard, balance, existing, now);
    return {
        id: accountId,
        budgetId,
        type: formValues.type,
        name: formValues.name,
        offBudgetGroup: formValues.offBudgetGroup,
        externalAccountId: formValues.externalAccountId,
        notes: formValues.notes,
        state: existing?.state ?? AccountState.ACTIVE,
        position: existing?.position ?? accountCount,
        assignments,
        transactions,
    };
}
/**
 * Constructs the initial transactions for a new account if required.
 *
 * Creates an adjustment transaction for new non-off-budget accounts with
 * a non-zero balance to set the starting balance.
 *
 * @param isNew - Whether this is a new account being created
 * @param balance - The starting balance value
 * @param accountId - The account identifier
 * @param budgetId - The budget identifier
 * @param now - Current date/time with user's timezone
 * @returns Array of initial transactions (may be empty)
 */
function constructTransactions(isNew, balance, accountId, budgetId, now) {
    if (isNew && balance !== 0) {
        return [
            {
                id: uuidv7(),
                budgetId,
                accountId,
                dateEpoch: now.toUnixInteger(),
                type: TransactionType.ADJUSTMENT,
                status: TransactionStatus.CLEARED,
                payee: 'Initial Balance',
                inflow: balance > 0 ? balance : 0,
                outflow: balance < 0 ? Math.abs(balance) : 0,
            },
        ];
    }
    return [];
}
/**
 * Constructs the initial assignments for a new account if required.
 *
 * Creates a negative assignment for new non-off-budget accounts with
 * a negative balance to ensure the budget correctly reflects the
 * starting debt situation.
 *
 * @param isNew - Whether this is a new account being created
 * @param isCreditCard - Whether the account is a credit card
 * @param balance - The starting balance value
 * @param existing - The existing account (if any)
 * @param now - Current date/time with user's timezone
 * @returns Array of assignments for the account
 */
function constructAssignments(isNew, isCreditCard, balance, existing, now) {
    if (isNew && isCreditCard && balance < 0) {
        return [
            {
                month: now.month,
                year: now.year,
                amount: balance,
            },
        ];
    }
    return existing?.assignments ?? [];
}
